import { Input, Slider } from '@mui/material'
import { clamp } from 'lodash-es'
import type { CSSProperties, ReactNode } from 'react'
import { useEffect, useState } from 'react'
import type {
  SliderProps as AriaSliderProps,
  LabelProps,
  SliderOutputProps,
  SliderThumbProps,
} from 'react-aria-components'
import {
  Slider as AriaSlider,
  composeRenderProps,
  Label,
  SliderOutput,
  SliderThumb,
  SliderTrack,
} from 'react-aria-components'
import { twMerge } from 'tailwind-merge'
import { tv } from 'tailwind-variants'

const sliderStyles = tv({
  base: 'orientation-horizontal:grid orientation-vertical:flex grid-cols-[1fr_auto] flex-col items-center gap-2',
})

const sliderOutputStyles = tv({
  base: 'text-sm  font-medium orientation-vertical:hidden',
})

const trackStyles = tv({
  base: 'absolute top-[50%] translate-y-[-50%] w-full rounded-full',
  variants: {
    variant: {
      'half-filled': 'h-[3px] dark:bg-neutral-400/50 bg-neutral-500/50 ',
    },
  },
})

const trackFilledStyles = tv({
  base: 'absolute top-[50%] translate-y-[-50%] rounded-full',
  variants: {
    variant: {
      'half-filled':
        'h-[3px] bg-neutral-800 dark:bg-neutral-50 w-[--thumb-percent]',
    },
  },
})

const thumbStyles = tv({
  base: 'group-orientation-horizontal:mt-4 group-orientation-vertical:ml-3 rounded-full',
  variants: {
    variant: {
      'half-filled':
        'size-4 border-[3px] border-neutral-800 dark:border-neutral-50 bg-neutral-800 dark:bg-neutral-50 group-orientation-horizontal:mt-4 cursor-pointer',
    },
  },
})

type UISliderProps<T> = AriaSliderProps<T> & {
  label?: ReactNode
  labelProps?: LabelProps
  sliderOutputProps?: SliderOutputProps
  sliderThumbProps?: SliderThumbProps
  variant?: 'half-filled'
}

export function UISlider<T extends number | number[]>({
  label,
  sliderOutputProps,
  sliderThumbProps,
  variant,
  ...props
}: UISliderProps<T>) {
  return (
    <AriaSlider
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        sliderStyles({ ...renderProps, className }),
      )}
    >
      {label && <Label {...props.labelProps}>{label}</Label>}
      <SliderOutput
        {...sliderOutputProps}
        className={composeRenderProps(
          sliderOutputProps?.className,
          (className, renderProps) =>
            sliderOutputStyles({ ...renderProps, className }),
        )}
      >
        {sliderOutputProps?.children ??
          (({ state }) =>
            state.values
              .map((_, i) => state.getThumbValueLabel(i))
              .join(' – '))}
      </SliderOutput>
      <SliderTrack className="group col-span-2 orientation-horizontal:h-6 orientation-vertical:w-6 orientation-vertical:h-64 flex items-center">
        {({ state, ...renderProps }) => (
          <>
            {/* track */}
            <div className={trackStyles({ ...renderProps, variant })} />
            {/* fill */}
            <div
              className={trackFilledStyles({ ...renderProps, variant })}
              style={
                {
                  '--thumb-percent': state.getThumbPercent(0) * 100 + '%',
                } as CSSProperties
              }
            />
            {state.values.map((_, i) => (
              <SliderThumb
                key={i}
                index={i}
                // TODO: Implement aria-label if needed
                // aria-label={thumbLabels?.[i]}
                className={thumbStyles({ ...renderProps, variant })}
                {...sliderThumbProps}
              />
            ))}
          </>
        )}
      </SliderTrack>
    </AriaSlider>
  )
}

/**
 * TODO: To be replaced with RAC
 * @param param0
 * @returns
 */
export function ValueSliderWithInput({
  min,
  max,
  step,
  value,
  onChange,
  sliderClassName,
  disabled,
  className,
  isInteger,
}: {
  min: number
  max: number
  step: number
  value?: number
  onChange?: (value: number) => void
  sliderClassName?: string
  disabled?: boolean
  className?: string
  /**
   * Value will be rounded to integer on blur
   */
  isInteger?: boolean
}) {
  const [inputValue, setInputValue] = useState(value)

  useEffect(() => {
    setInputValue(value)
  }, [value])

  function onInputBlur() {
    let clampedVal = clamp(Number(inputValue), min, max)
    if (isInteger) clampedVal = Math.round(clampedVal)
    onChange?.(clampedVal)
  }

  return (
    <div className={twMerge('flex gap-8', className)}>
      <Slider
        disabled={disabled}
        className={sliderClassName}
        value={value}
        onChange={(_, value) => {
          onChange?.(value as number)
        }}
        min={min}
        max={max}
        step={step}
        valueLabelDisplay="auto"
      />
      <Input
        disabled={disabled}
        type="number"
        className="w-[60px]"
        value={inputValue}
        onChange={e => {
          // Firefox doesn't autofocus the input when clicking the arrow buttons.
          // So we focus it manually when the value is changed to ensure the
          // functionality of this component.
          e.target.focus()
          setInputValue(Number(e.target.value))
        }}
        onBlur={onInputBlur}
        inputProps={{
          min,
          max,
        }}
      />
    </div>
  )
}
